import gsap from 'gsap'

export class Header {
  constructor() {
    this._header = document.querySelector('header')
    this._burger = document.querySelector('#burger')
    this._mobileMenu = document.querySelector('.h-nav__mobile')
    this._itemsWithChildren = document.querySelectorAll('.menu-item-has-children')

    if (this._itemsWithChildren.length) {
      this._itemsWithChildren.forEach(item => {
        this.handleSubmenu(item)
      })
    }

    this._desktop = window.matchMedia('(min-width: 1024px)')
    this._burger.addEventListener('click', this.handleBurger)
    this._mobileMenu.addEventListener('click', this.handleMobileLink)
    this._desktop.addEventListener('change', () => {
      if (this._desktop.matches) this.setMenu(false)
    })
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.setMenu(false)
    })
    window.addEventListener('resize', this.syncHeaderHeight)
    window.addEventListener('scroll', this.scroll, { passive: true })
    this.syncHeaderHeight()
    this.scroll()
  }

  syncHeaderHeight = () => {
    document.documentElement.style.setProperty('--header-h', `${this._header.offsetHeight}px`)
  }

  setMenu = (open) => {
    this._mobileMenu.classList.toggle('is-active', open)
    this._burger.setAttribute('aria-expanded', open ? 'true' : 'false')
    this._burger.setAttribute('aria-label', open ? 'Fermer le menu' : 'Ouvrir le menu')
    document.documentElement.classList.toggle('is-menu-open', open)
    this.syncHeaderHeight()
  }

  handleBurger = () => {
    this.setMenu(!this._mobileMenu.classList.contains('is-active'))
  }

  handleMobileLink = (event) => {
    const link = event.target.closest('a')

    if (!link || link.matches('.menu-item--mega > a')) return

    this.setMenu(false)
  }

  // Deux seuils : un seul rebascule la classe si le scroll bouge pendant la transition.
  scroll = () => {
    const y = window.scrollY
    const compact = this._header.classList.contains('scrolled')

    if (!compact && y > 140) {
      this._header.classList.add('scrolled')
    } else if (compact && y < 100) {
      this._header.classList.remove('scrolled')
    }

    this.syncHeaderHeight()
  }

  handleSubmenu = (item) => {
    if (item.classList.contains('menu-item--mega')) {
      this.handleMega(item)
      return
    }

    const submenu = item.querySelector('.sub-menu')
    const link = item.querySelector(':scope > a')
    const isMobile = /Mobi|Android/i.test(navigator.userAgent)

    const openAnimation = gsap.to(submenu, {
      height: 'auto',
      duration: 0.5,
      autoAlpha: 1,
      ease: 'power2.inOut',
      paused: true,
    })

    let clicked = false;

    link.addEventListener('click', (e) => {
      if (isMobile && !clicked) {
        e.preventDefault()
      }
      clicked = !clicked
    })

    item.addEventListener('mouseover', (e) => {
      openAnimation.play()
    })

    item.addEventListener('mouseleave', (e) => {
      openAnimation.reverse()
      clicked = false
    })

  }

  mountMegaScroller = (item, panel) => {
    const frame = document.createElement('div')
    frame.className = 'menu-mega__frame'
    panel.before(frame)
    frame.append(panel)

    const button = (className, label) => {
      const el = document.createElement('button')
      el.type = 'button'
      el.className = `menu-mega__arrow ${className}`
      el.setAttribute('aria-label', label)
      el.hidden = true
      return el
    }

    const prev = button('menu-mega__arrow--prev', 'Produits précédents')
    const next = button('menu-mega__arrow--next', 'Produits suivants')
    frame.append(prev, next)

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const page = () => Math.max(panel.clientWidth * 0.8, 240)

    const scrollBy = (direction) => {
      panel.scrollBy({ left: direction * page(), behavior: reduced ? 'auto' : 'smooth' })
    }

    prev.addEventListener('click', (event) => {
      event.preventDefault()
      scrollBy(-1)
    })
    next.addEventListener('click', (event) => {
      event.preventDefault()
      scrollBy(1)
    })

    const update = () => {
      const max = panel.scrollWidth - panel.clientWidth
      const canPrev = panel.scrollLeft > 4
      const canNext = panel.scrollLeft < max - 4
      prev.hidden = !canPrev
      next.hidden = !canNext
      frame.classList.toggle('can-prev', canPrev)
      frame.classList.toggle('can-next', canNext)
    }

    panel.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    panel.addEventListener('wheel', (event) => {
      if (panel.scrollWidth <= panel.clientWidth + 4) return
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return
      const atStart = panel.scrollLeft <= 0 && event.deltaY < 0
      const atEnd = panel.scrollLeft >= panel.scrollWidth - panel.clientWidth - 1 && event.deltaY > 0
      if (atStart || atEnd) return
      event.preventDefault()
      panel.scrollLeft += event.deltaY
    }, { passive: false })

    let drag = null
    panel.addEventListener('dragstart', (event) => event.preventDefault())
    panel.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return
      drag = { id: event.pointerId, x: event.clientX, scroll: panel.scrollLeft, moved: false }
    })
    panel.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return
      const dx = event.clientX - drag.x
      if (!drag.moved && Math.abs(dx) <= 5) return
      if (!drag.moved) {
        drag.moved = true
        panel.classList.add('is-dragging')
        panel.setPointerCapture(event.pointerId)
      }
      panel.scrollLeft = drag.scroll - dx
    })
    const endDrag = () => {
      if (!drag) return
      const moved = drag.moved
      drag = null
      panel.classList.remove('is-dragging')
      if (!moved) return
      const stopClick = (event) => {
        event.preventDefault()
        event.stopPropagation()
        panel.removeEventListener('click', stopClick, true)
      }
      panel.addEventListener('click', stopClick, true)
    }
    panel.addEventListener('pointerup', endDrag)
    panel.addEventListener('pointercancel', endDrag)

    item.addEventListener('mouseenter', () => requestAnimationFrame(update))
    update()
  }

  handleMega = (item) => {
    const link = item.querySelector(':scope > a')
    const panel = item.querySelector(':scope > .sub-menu')
    const desktop = window.matchMedia('(min-width: 1024px)')

    if (panel && item.closest('.site-header__nav')) {
      this.mountMegaScroller(item, panel)
    }

    let closeTimer = 0

    const setOpen = (open) => {
      window.clearTimeout(closeTimer)
      item.classList.toggle('is-open', open)
      link?.setAttribute('aria-expanded', open ? 'true' : 'false')
    }

    const scheduleClose = () => {
      window.clearTimeout(closeTimer)
      closeTimer = window.setTimeout(() => setOpen(false), 500)
    }

    link?.setAttribute('aria-haspopup', 'true')
    link?.setAttribute('aria-expanded', 'false')

    link?.addEventListener('click', (event) => {
      event.preventDefault()
      if (!desktop.matches) {
        setOpen(!item.classList.contains('is-open'))
      }
    })

    item.addEventListener('mouseenter', () => {
      if (desktop.matches) setOpen(true)
    })
    item.addEventListener('mouseleave', () => {
      if (desktop.matches) scheduleClose()
    })
    item.addEventListener('focusin', () => {
      if (desktop.matches) setOpen(true)
    })
    item.addEventListener('focusout', (event) => {
      if (desktop.matches && !item.contains(event.relatedTarget)) setOpen(false)
    })
    item.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
        link?.focus()
      }
    })
  }
}
